// ─── Enums ──────────────────────────────────────────────────────────────────

export enum IdmAction {
	CREATE = 'create',
	MODIFY = 'modify',
	DELETE = 'delete',
}

export enum IdmRequestStatus {
	PENDING_CONFIRMATION = 'pending_confirmation',
	EXECUTING = 'executing',
	COMPLETED = 'completed',
	PARTIAL = 'partial',
	FAILED = 'failed',
	EXPIRED = 'expired',
	CANCELLED = 'cancelled',
}

export enum IdmSignalSeverity {
	RED = 'red',
	YELLOW = 'yellow',
}

export enum IdmSignalCode {
	ONLY_ADMIN_REMOVAL = 'ONLY_ADMIN_REMOVAL',
	EMPLOYEE_ALREADY_EXISTS = 'EMPLOYEE_ALREADY_EXISTS',
	LOW_PROVIDER_ADMIN_COUNT = 'LOW_PROVIDER_ADMIN_COUNT',
	HIGH_RISK_ACC_DEPROVISION = 'HIGH_RISK_ACC_DEPROVISION',
	PROJECT_INACTIVE = 'PROJECT_INACTIVE',
	PROJECT_NOT_FOUND = 'PROJECT_NOT_FOUND',
	PROVIDER_NOT_FOUND = 'PROVIDER_NOT_FOUND',
	PROVIDER_DEPRECATED = 'PROVIDER_DEPRECATED',
	PROVIDER_NEAR_DEPRECATION = 'PROVIDER_NEAR_DEPRECATION',
	GROUP_WILL_BE_EMPTY = 'GROUP_WILL_BE_EMPTY',
}

// ─── Interfaces ─────────────────────────────────────────────────────────────

export interface IdmRequestInput {
	email: string
	action: IdmAction
	entraDirectoryId?: string
	contractType?: string
	geoLocation?: string
	department?: string
	leadershipType?: string
	providers?: Array<{ providerId: string; role: string }>
	projects?: Array<{ projectId: string; environment?: string | null }>
}

export interface IdmSignal {
	severity: IdmSignalSeverity
	code: IdmSignalCode
	message: string
	affectedGroup?: string
	approvalOwner?: string
	remainingAdminName?: string
	remainingAdminEmail?: string
	requiresOverride: boolean
}

export interface IdmGroupChange {
	groupName: string
	isNew?: boolean
	approvalRequired?: boolean
	approvalOwner?: string
	currentMemberCount?: number
}

export interface IdmAccChange {
	idmGroup: string
	accGroup: string
	tool: string
	role: string
	owner: string
	highRisk: boolean
	isElevatedAccess: boolean
	provisioningMethod: string
}

export interface IdmConfirmationPayload {
	requestId: string
	expiresAt: string
	employee: { email: string; entraDirectoryId: string; displayName: string | null }
	action: IdmAction
	signals: IdmSignal[]
	validationWarnings: string[]
	groupsToCreate: Array<{ groupName: string; category: string; reason: string }>
	groupsToAdd: IdmGroupChange[]
	groupsToRemove: IdmGroupChange[]
	accToProvision: IdmAccChange[]
	accToDeprovision: IdmAccChange[]
	summary: {
		groupsToCreate: number
		groupsToAdd: number
		groupsToRemove: number
		accToProvision: number
		accToDeprovision: number
		redSignals: number
		yellowSignals: number
	}
}

export interface IdmExecutionSummary {
	requestId: string
	status: IdmRequestStatus
	completedAt: string
	employee: { email: string; entraDirectoryId: string }
	action: IdmAction
	results: {
		groupsCreated: Array<{ groupName: string; entraGroupId: string }>
		groupsAdded: Array<{ groupName: string; success: boolean }>
		groupsRemoved: Array<{ groupName: string; success: boolean }>
		groupsFailed: Array<{ groupName: string; error: string }>
	}
	accTicketEventsEmitted: number
	notificationEventsEmitted: number
}

export interface IIdmRequest {
	requestId: string
	email: string
	entraDirectoryId: string | null
	action: IdmAction
	status: IdmRequestStatus
	inputPayload: IdmRequestInput
	confirmationPayload: IdmConfirmationPayload | null
	executionResult: IdmExecutionSummary | null
	signals: IdmSignal[]
	overrideSignals: string[] | null
	overrideJustification: string | null
	expiresAt: Date
	confirmedAt: Date | null
	completedAt: Date | null
	cancelledAt: Date | null
	requestedBy: string | null
	createdAt: Date
	updatedAt: Date
}
